import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { supabase } from '../../lib/supabase';
import { getAllowedTeams, getTeamOptions, applyTeamFilter } from '../../utils/teamPermissions';
import { 
  ArrowLeft,
  Calendar,
  Clock,
  MapPin,
  PlusCircle,
  Users,
  CheckCircle,
  XCircle,
  AlertCircle,
  Edit2,
  Trash2,
  ClipboardList
} from 'lucide-react';

const TrainingsList = () => {
  const navigate = useNavigate();
  const { userProfile } = useAuth();
  const [trainings, setTrainings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterTeam, setFilterTeam] = useState(() => {
    // Entrenador de Amateur empieza con "Todos", otros entrenadores con su equipo
    if (userProfile?.role === 'entrenador') {
      return userProfile.team === 'Amateur' ? 'Todos' : userProfile.team;
    }
    return 'Todos';
  });
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [trainingToDelete, setTrainingToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const teams = useMemo(() => getTeamOptions(userProfile), [userProfile]);

  useEffect(() => {
    if (userProfile) {
      fetchTrainings();
    }
  }, [filterTeam, userProfile]);

  const fetchTrainings = async () => {
    try {
      setLoading(true);

      // Calcular allowedTeams DENTRO de la función
      const allowedTeams = getAllowedTeams(userProfile);

      let query = supabase
        .from('trainings')
        .select('*')
        .order('date', { ascending: false })
        .order('start_time', { ascending: false });

      query = applyTeamFilter(query, allowedTeams, filterTeam);

      const { data, error } = await query;

      if (error) throw error;

      // Para cada entrenamiento, obtener estadísticas de asistencia
      const trainingsWithStats = await Promise.all(
        (data || []).map(async (training) => {
          const { data: attendance, error: attError } = await supabase
            .from('attendance')
            .select('status')
            .eq('training_id', training.id);

          if (attError) console.error('Error al cargar asistencias:', attError);

          const stats = {
            total: attendance?.length || 0,
            presente: attendance?.filter(a => a.status === 'presente').length || 0,
            ausente: attendance?.filter(a => a.status === 'ausente').length || 0,
            justificado: attendance?.filter(a => a.status === 'justificado').length || 0,
            lesionado: attendance?.filter(a => a.status === 'lesionado').length || 0,
            tardanza: attendance?.filter(a => a.status === 'tardanza').length || 0,
          };

          return { ...training, stats };
        })
      );

      setTrainings(trainingsWithStats);
    } catch (error) {
      console.error('Error al cargar entrenamientos:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!trainingToDelete) return;

    try {
      setDeleting(true);

      const { error } = await supabase
        .from('trainings')
        .delete()
        .eq('id', trainingToDelete.id);

      if (error) throw error;

      setTrainings(trainings.filter(t => t.id !== trainingToDelete.id));
      setShowDeleteModal(false);
      setTrainingToDelete(null);
    } catch (error) {
      console.error('Error al eliminar entrenamiento:', error);
      alert('Error al eliminar el entrenamiento');
    } finally {
      setDeleting(false);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('es-ES', {
      weekday: 'long',
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    });
  };

  const formatTime = (timeString) => {
    if (!timeString) return '';
    return timeString.substring(0, 5);
  };

  const getTeamColor = (team) => {
    switch(team) {
      case 'Juvenil':
        return 'bg-blue-100 text-blue-800 border-blue-200';
      case 'Cadete':
        return 'bg-green-100 text-green-800 border-green-200';
      case 'Amateur':
        return 'bg-purple-100 text-purple-800 border-purple-200';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const getTypeColor = (type) => {
    switch(type) {
      case 'Entrenamiento':
        return 'bg-blue-50 text-blue-700';
      case 'Partido amistoso':
        return 'bg-yellow-50 text-yellow-700';
      case 'Partido oficial':
        return 'bg-red-50 text-red-700';
      default:
        return 'bg-gray-50 text-gray-700';
    }
  };

  const isPast = (dateString, timeString) => {
    const trainingDateTime = new Date(`${dateString}T${timeString}`);
    return trainingDateTime < new Date();
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Cargando entrenamientos...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <button
                onClick={() => navigate('/dashboard')}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <ArrowLeft className="w-5 h-5 text-gray-600" />
              </button>
              <div>
                <h1 className="text-2xl font-bold text-gray-900">Entrenamientos y Asistencias</h1>
                <p className="text-sm text-gray-600">{trainings.length} entrenamientos registrados</p>
              </div>
            </div>

            <button
              onClick={() => navigate('/trainings/new')}
              className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              <PlusCircle className="w-5 h-5" />
              Nuevo Entrenamiento
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Filtros */}
        {userProfile?.role !== 'entrenador' && (
          <div className="bg-white rounded-xl shadow-sm p-6 mb-6">
            <div className="flex items-center gap-4">
              <label className="text-sm font-medium text-gray-700">Filtrar por equipo:</label>
              <select
                value={filterTeam}
                onChange={(e) => setFilterTeam(e.target.value)}
                className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                {teams.map(team => (
                  <option key={team} value={team}>{team}</option>
                ))}
              </select>
            </div>
          </div>
        )}

        {/* Lista de entrenamientos */}
        {trainings.length === 0 ? (
          <div className="bg-white rounded-xl shadow-sm p-12 text-center">
            <Calendar className="w-16 h-16 text-gray-400 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-gray-900 mb-2">No hay entrenamientos</h3>
            <p className="text-gray-600 mb-6">Comienza creando el primer entrenamiento</p>
            <button
              onClick={() => navigate('/trainings/new')}
              className="inline-flex items-center gap-2 px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              <PlusCircle className="w-5 h-5" />
              Crear Entrenamiento
            </button>
          </div>
        ) : (
          <div className="space-y-4">
            {trainings.map((training) => {
              const past = isPast(training.date, training.start_time);
              const attendanceRate = training.stats.total > 0 
                ? Math.round((training.stats.presente / training.stats.total) * 100) 
                : 0;

              return (
                <div
                  key={training.id}
                  className={`bg-white rounded-xl shadow-sm overflow-hidden hover:shadow-md transition-shadow ${
                    past ? 'opacity-75' : ''
                  }`}
                >
                  <div className="p-6">
                    <div className="flex items-start justify-between mb-4">
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-2">
                          <span className={`px-3 py-1 rounded-full text-xs font-medium border ${getTeamColor(training.team)}`}>
                            {training.team}
                          </span>
                          <span className={`px-3 py-1 rounded-full text-xs font-medium ${getTypeColor(training.type)}`}>
                            {training.type || 'Entrenamiento'}
                          </span>
                          {past && (
                            <span className="px-3 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-600">
                              Finalizado
                            </span>
                          )}
                        </div>

                        <div className="flex items-center gap-6 text-gray-600 mb-3">
                          <div className="flex items-center gap-2">
                            <Calendar className="w-4 h-4" />
                            <span className="text-sm font-medium">{formatDate(training.date)}</span>
                          </div>
                          <div className="flex items-center gap-2">
                            <Clock className="w-4 h-4" />
                            <span className="text-sm">
                              {formatTime(training.start_time)} - {formatTime(training.end_time)}
                            </span>
                          </div>
                          {training.location && (
                            <div className="flex items-center gap-2">
                              <MapPin className="w-4 h-4" />
                              <span className="text-sm">{training.location}</span>
                            </div>
                          )}
                        </div>

                        {training.description && (
                          <p className="text-sm text-gray-600 mb-3">{training.description}</p>
                        )}

                        {/* Estadísticas de asistencia */}
                        {training.stats.total > 0 && (
                          <div className="flex items-center gap-4">
                            <div className="flex items-center gap-2">
                              <Users className="w-4 h-4 text-gray-500" />
                              <span className="text-sm text-gray-600">{training.stats.total} jugadores</span>
                            </div>
                            <div className="flex items-center gap-3 text-sm">
                              <div className="flex items-center gap-1">
                                <CheckCircle className="w-4 h-4 text-green-600" />
                                <span className="text-green-600 font-medium">{training.stats.presente}</span>
                              </div>
                              <div className="flex items-center gap-1">
                                <XCircle className="w-4 h-4 text-red-600" />
                                <span className="text-red-600 font-medium">{training.stats.ausente}</span>
                              </div>
                              {training.stats.justificado > 0 && (
                                <div className="flex items-center gap-1">
                                  <AlertCircle className="w-4 h-4 text-orange-600" />
                                  <span className="text-orange-600 font-medium">{training.stats.justificado}</span>
                                </div>
                              )}
                            </div>
                            <div className="ml-auto">
                              <div className="flex items-center gap-2">
                                <div className="w-32 h-2 bg-gray-200 rounded-full overflow-hidden">
                                  <div 
                                    className="h-full bg-green-600 rounded-full"
                                    style={{ width: `${attendanceRate}%` }}
                                  ></div>
                                </div>
                                <span className="text-sm font-medium text-gray-700">{attendanceRate}%</span>
                              </div>
                            </div>
                          </div>
                        )}
                      </div>

                      {/* Acciones */}
                      <div className="flex items-center gap-2 ml-6">
                        <button
                          onClick={() => navigate(`/trainings/${training.id}/attendance`)}
                          className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                          title="Pasar lista"
                        >
                          <ClipboardList className="w-5 h-5" />
                        </button>
                        <button
                          onClick={() => navigate(`/trainings/edit/${training.id}`)}
                          className="p-2 text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                          title="Editar"
                        >
                          <Edit2 className="w-4 h-4" />
                        </button>
                        {userProfile?.role === 'admin' && (
                          <button
                            onClick={() => {
                              setTrainingToDelete(training);
                              setShowDeleteModal(true);
                            }}
                            className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                            title="Eliminar"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        )}
                      </div>
                    </div>

                    {training.notes && (
                      <div className="mt-3 pt-3 border-t">
                        <p className="text-sm text-gray-600">
                          <strong>Notas:</strong> {training.notes}
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Modal de confirmación de eliminación */}
      {showDeleteModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-md w-full p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center">
                <Trash2 className="w-6 h-6 text-red-600" />
              </div>
              <div>
                <h3 className="text-lg font-bold text-gray-900">Eliminar Entrenamiento</h3>
                <p className="text-sm text-gray-600">Esta acción no se puede deshacer</p>
              </div>
            </div>

            <div className="bg-gray-50 rounded-lg p-4 mb-6">
              <p className="text-sm text-gray-700 mb-2">
                <strong>Fecha:</strong> {formatDate(trainingToDelete?.date)}
              </p>
              <p className="text-sm text-gray-700">
                <strong>Equipo:</strong> {trainingToDelete?.team}
              </p>
            </div>

            <div className="flex gap-3">
              <button
                onClick={() => {
                  setShowDeleteModal(false);
                  setTrainingToDelete(null);
                }}
                disabled={deleting}
                className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50"
              >
                Cancelar
              </button>
              <button
                onClick={handleDelete}
                disabled={deleting}
                className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50"
              >
                {deleting ? 'Eliminando...' : 'Eliminar'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TrainingsList;